import { useCallback } from "react";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import { AutoPlayType, PromoState } from "../../../globals";
import { usePromoContext } from "../../contexts/PromoContext";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import { useHlsContext } from "../../contexts/HlsContext";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { useVideoDataContext } from "../../contexts/VideoDataContext";

export const useTransitionToVideo = ({
  setRenderControls,
  isCarousel,
  autoplay,
  play,
  playCallback = () => {}
}) => {
  const { autoplayState } = useAutoplayContext();
  const { setPromoState } = usePromoContext();
  const { sendAnalyticsEvent, sendVideoStartEvent } =
    useVideoAnalyticsContext();
  const { isSupported } = useHlsContext();
  const videoElementRef = useVideoElementRefContext();
  const { uniqueId } = useVideoDataContext();

  const loadAndPlayVideo = useCallback(() => {
    const videoElement = videoElementRef.current;
    const loadOnce = () => {
      videoElement.removeEventListener("loadstart", loadOnce);
      play();
    };
    videoElement.addEventListener("loadstart", loadOnce);
    videoElement.load();
  }, [videoElementRef, play]);

  const transitionToVideo = useCallback(
    ({ analyticsData, isAutoplay } = {}) => {
      if (!autoplayState.isLivePreview) {
        setPromoState(PromoState.HIDDEN);
      }

      setRenderControls(
        !(
          autoplayState.type === AutoPlayType.LOOP_NO_CONTROLS ||
          autoplayState.isLivePreview
        )
      );

      // carousel videos send specialized start events in the carousel component
      if (!isCarousel) {
        sendVideoStartEvent(
          autoplayState,
          analyticsData
            ? { ...analyticsData, videostart_id: uniqueId }
            : { videostart_id: uniqueId }
        );
      }
      if (isSupported) {
        isAutoplay ? autoplay() : play();
      } else {
        loadAndPlayVideo();
      }

      playCallback();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoplayState.isLivePreview,
      autoplayState.type,
      playCallback,
      sendAnalyticsEvent,
      isCarousel
    ]
  );

  return transitionToVideo;
};
